import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { DateRangePicker } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import merge from 'lodash/merge';
import ReactApexChart from 'react-apexcharts';
import { Card, CardHeader, Box } from '@mui/material';
import { BaseOptionChart } from '../../charts';
import { WebAlertService } from '../../../application/WebAlertService';

const INITIAL_CHART_DATA = [
  { name: '主動推播', type: 'area', fill: 'gradient', data: [] },
  { name: '我要訂車', type: 'column', fill: 'solid', data: [] },
  { name: '車輛查詢', type: 'column', fill: 'solid', data: [] },
  { name: '訂單查詢', type: 'column', fill: 'solid', data: [] }
];

const getChartOptions = (chartLabels, chartData) => merge(BaseOptionChart(), {
  stroke: { width: [4, 4], curve: 'smooth' },
  plotOptions: { bar: { columnWidth: '60%', borderRadius: 3 } },
  fill: { type: chartData.map((i) => i.fill) },
  labels: chartLabels,
  xaxis: { type: 'string' },
  yaxis: [{ title: { text: '主動推播' } }],
  tooltip: {
    shared: true,
    intersect: false,
    y: {
      formatter: (y) => (typeof y !== 'undefined' ? `${y.toFixed(0)} 次` : y),
    },
  },
});
const createWebAlertService = (host, region) => new WebAlertService(host, region);

export default function AppWebalert({ title }) {
  const Region = localStorage.getItem('region');
  const host = localStorage.getItem('apiurl');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: moment().tz('Asia/Taipei').add(-14, 'days'),
    endDate: moment().tz('Asia/Taipei').add(1, 'days')
  });
  const [chartLabels, setChartLabels] = useState([]);
  const [focusedInput, setFocusedInput] = useState(null);
  const [chartData, setChartData] = useState(INITIAL_CHART_DATA);

  const webAlertService = useMemo(() => createWebAlertService(host, Region), [host, Region]);

  const fetchData = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const statistics = await webAlertService.getStatistics(
        dateRange.startDate.format('YYYY-MM-DD'),
        dateRange.endDate.format('YYYY-MM-DD')
      );

      setChartLabels(statistics.map(stat => stat.statisticsdate));
      setChartData([
        { ...INITIAL_CHART_DATA[0], data: statistics.map(stat => stat.count) },
        { ...INITIAL_CHART_DATA[1], data: statistics.map(stat => stat.booking) },
        { ...INITIAL_CHART_DATA[2], data: statistics.map(stat => stat.cargps) },
        { ...INITIAL_CHART_DATA[3], data: statistics.map(stat => stat.dirorder) }
      ]);
    } catch (error) {
      setError('取得資料失敗');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate, webAlertService]); // 移除 isLoading 依賴

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 30000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  const handleLINEateventClick = () => {
    fetchData();
  };

  return (
    <Card>
      <Grid container alignItems="center">
        <Grid item xs={4}>
          <CardHeader title={title} subheader="" />
        </Grid>
        <Grid item xs={6}>
          <Box display="flex" alignItems="center">
            <Typography variant="body1">查詢區間：</Typography>
            <DateRangePicker
              startDate={dateRange.startDate}
              startDateId="start_date_id"
              endDate={dateRange.endDate}
              endDateId="end_date_id"
              onDatesChange={({ startDate, endDate }) =>
                setDateRange({ startDate, endDate })
              }
              focusedInput={focusedInput}
              onFocusChange={setFocusedInput}
              isOutsideRange={() => false}
              numberOfMonths={1}
            />
          </Box>
        </Grid>
        <Grid item xs={2}>
          <Button variant="contained" color="primary" onClick={handleLINEateventClick}>
            查詢
          </Button>
        </Grid>
      </Grid>
      <Box position="relative" sx={{ p: 3, pb: 1 }} dir="ltr">
        <ReactApexChart
          type="line"
          series={chartData}
          options={getChartOptions(chartLabels, chartData)}
          height={430}
        />
        {isLoading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            bgcolor="rgba(0, 0, 0, 0.5)"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
            <Typography variant="h6" color="textPrimary" sx={{ color: '#fff', ml: 1 }}>
              載入中...
            </Typography>
          </Box>
        )}
        {error && (
          <Typography color="error" align="center" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </Box>
    </Card>
  );
}

AppWebalert.propTypes = {
  title: PropTypes.string,
};